import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, MessageSquareWarning, Sparkles, Paperclip, X, ImageIcon, Plus, MessagesSquare, Pencil, Trash2, Check } from "lucide-react";

const SUGGESTIONS = [
  "J'ai fait tomber mon iPhone, l'écran est cassé",
  "Accrochage en stationnement sans tiers identifié",
  "Ma valise a été perdue à l'aéroport",
  "Dégât des eaux sous mon évier",
];

function renderContent(text) {
  return text.split("\n").map((line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={i} className={line.trim() === "" ? "h-2" : "leading-relaxed"}>
        {parts.map((p, j) =>
          p.startsWith("**") && p.endsWith("**") ? (
            <strong key={j} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>
          ) : (
            <span key={j}>{p}</span>
          )
        )}
      </p>
    );
  });
}

export default function PepinView() {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [image, setImage] = useState(null); // {b64, name}
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const endRef = useRef();
  const fileRef = useRef();

  const loadConversations = async (selectFirst = true) => {
    try {
      const { data } = await api.get("/pepin/conversations");
      setConversations(data);
      if (selectFirst && data.length && !activeId) {
        setActiveId(data[0].id);
      }
      return data;
    } catch {
      return [];
    }
  };

  const loadMessages = async (id) => {
    if (!id) { setMessages([]); return; }
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/pepin/conversations/${id}/messages`);
      setMessages(data);
    } catch {
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  useEffect(() => { loadConversations(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadMessages(activeId); }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const selectConversation = (id) => {
    setActiveId(id);
    setSidebarOpen(false);
  };

  const newConversation = async () => {
    try {
      const { data } = await api.post("/pepin/conversations", { title: "Nouvelle conversation" });
      setConversations((c) => [data, ...c]);
      setActiveId(data.id);
      setMessages([]);
      setSidebarOpen(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Impossible de créer la conversation");
    }
  };

  const deleteConversation = async (id) => {
    try {
      await api.delete(`/pepin/conversations/${id}`);
      const remaining = conversations.filter((c) => c.id !== id);
      setConversations(remaining);
      if (activeId === id) {
        const next = remaining[0]?.id || null;
        setActiveId(next);
        if (!next) setMessages([]);
      }
      toast.success("Conversation supprimée");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const startRename = (c) => {
    setRenamingId(c.id);
    setRenameValue(c.title);
  };

  const confirmRename = async () => {
    const title = renameValue.trim();
    const id = renamingId;
    setRenamingId(null);
    if (!title || !id) return;
    try {
      await api.patch(`/pepin/conversations/${id}`, { title });
      setConversations((cs) => cs.map((c) => (c.id === id ? { ...c, title } : c)));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Erreur");
    }
  };

  const pickImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Choisissez une image"); return; }
    const reader = new FileReader();
    reader.onload = () => setImage({ b64: String(reader.result).split(",")[1], name: file.name });
    reader.readAsDataURL(file);
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if ((!msg && !image) || sending) return;
    const outImg = image;
    setInput("");
    setImage(null);
    setMessages((m) => [...m, { role: "user", content: msg + (outImg ? " 📎 [photo jointe]" : "") }]);
    setSending(true);
    try {
      const { data } = await api.post("/pepin/chat", {
        message: msg || "Voici une photo du sinistre.",
        image_base64: outImg?.b64 || null,
        conversation_id: activeId || null,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.content }]);
      if (!activeId && data.conversation_id) {
        setActiveId(data.conversation_id);
      }
      loadConversations(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Erreur de l'assistant");
      setMessages((m) => m.slice(0, -1));
    } finally {
      setSending(false);
    }
  };

  const activeTitle = conversations.find((c) => c.id === activeId)?.title || "Nouvelle conversation";

  return (
    <div className="animate-in-up" data-testid="pepin-view">
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Assistant Pépin</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Un pépin ? Demandez.</h1>
        </div>
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-4">
        {/* Conversations sidebar */}
        <aside className={`${sidebarOpen ? "block" : "hidden"} md:block`} data-testid="pepin-sidebar">
          <button
            data-testid="pepin-new-conversation"
            onClick={newConversation}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition"
          >
            <Plus className="w-4 h-4" /> Nouvelle conversation
          </button>
          <div className="mt-3 space-y-1 max-h-[40vh] md:max-h-[56vh] overflow-y-auto scrollbar-thin">
            {conversations.length === 0 && (
              <p className="text-xs text-muted-foreground px-2 py-3">Aucune conversation pour l'instant.</p>
            )}
            {conversations.map((c) => (
              <div
                key={c.id}
                data-testid={`pepin-conv-${c.id}`}
                className={`group flex items-center gap-1 rounded-xl border px-2.5 py-2 transition ${
                  activeId === c.id ? "border-primary bg-accent" : "border-transparent hover:bg-muted"
                }`}
              >
                {renamingId === c.id ? (
                  <input
                    data-testid="pepin-conv-rename-input"
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={confirmRename}
                    onKeyDown={(e) => { if (e.key === "Enter") confirmRename(); if (e.key === "Escape") setRenamingId(null); }}
                    className="flex-1 min-w-0 h-8 px-2 rounded-lg border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40"
                  />
                ) : (
                  <button
                    data-testid={`pepin-conv-select-${c.id}`}
                    onClick={() => selectConversation(c.id)}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  >
                    <MessagesSquare className="w-4 h-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm font-medium">{c.title}</span>
                  </button>
                )}
                {renamingId === c.id ? (
                  <button onMouseDown={(e) => e.preventDefault()} onClick={confirmRename} className="w-7 h-7 grid place-items-center rounded-lg text-emerald-600 hover:bg-muted shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <div className="flex items-center opacity-0 group-hover:opacity-100 transition shrink-0">
                    <button data-testid={`pepin-conv-rename-${c.id}`} onClick={() => startRename(c)} className="w-7 h-7 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`pepin-conv-delete-${c.id}`} onClick={() => deleteConversation(c.id)} className="w-7 h-7 grid place-items-center rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Chat */}
        <section className="min-w-0">
          {/* Mobile conversations toggle */}
          <div className="md:hidden mb-3 flex items-center gap-2">
            <button
              data-testid="pepin-conversations-toggle"
              onClick={() => setSidebarOpen((s) => !s)}
              className="flex items-center gap-2 h-10 px-3.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition"
            >
              <MessagesSquare className="w-4 h-4" /> Conversations ({conversations.length})
            </button>
            <span className="text-sm text-muted-foreground truncate">{activeTitle}</span>
          </div>

          <div className="rounded-2xl border border-border bg-card flex flex-col h-[58vh] md:h-[62vh]">
            <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
              {loadingMsgs ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : messages.length === 0 && !sending ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4 text-muted-foreground">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary grid place-items-center">
                    <MessageSquareWarning className="w-7 h-7" />
                  </div>
                  <p className="text-sm max-w-xs">Décrivez votre incident, l'IA vous dit si vous êtes couvert et par quel contrat.</p>
                  <div className="flex flex-wrap justify-center gap-2 max-w-lg">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid="pepin-suggestion" onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/50 hover:bg-accent transition">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      data-testid={`msg-${m.role}`}
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                        m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted text-foreground rounded-bl-md space-y-0.5"
                      }`}
                    >
                      {m.role === "assistant" ? renderContent(m.content) : m.content}
                    </div>
                  </div>
                ))
              )}

              {sending && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2 text-sm text-muted-foreground">
                    <Sparkles className="w-4 h-4 animate-pulse text-ochre" /> L'assistant analyse vos contrats…
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="border-t border-border p-3">
              {image && (
                <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-muted text-sm w-fit" data-testid="image-preview">
                  <ImageIcon className="w-4 h-4 text-primary" />
                  <span className="truncate max-w-[200px]">{image.name}</span>
                  <button onClick={() => setImage(null)} className="text-muted-foreground hover:text-destructive">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="pepin-image-input" onChange={(e) => pickImage(e.target.files?.[0])} />
                <button
                  data-testid="pepin-attach-btn"
                  onClick={() => fileRef.current?.click()}
                  className="h-11 w-11 grid place-items-center rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition shrink-0"
                  title="Joindre une photo du sinistre"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <input
                  data-testid="pepin-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ex : j'ai cassé l'écran de mon téléphone…"
                  className="flex-1 h-11 px-4 rounded-xl bg-muted text-sm outline-none focus:ring-2 focus:ring-ring/40"
                />
                <button
                  data-testid="pepin-send-btn"
                  onClick={() => send()}
                  disabled={sending || (!input.trim() && !image)}
                  className="h-11 w-11 grid place-items-center rounded-xl bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition shrink-0"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
